import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface SectionPanelProps {
	title: string;
	caption?: string;
	action?: ReactNode;
	link?: { label: string; to: string };
	children: ReactNode;
	/** Gives the primary band a restrained warm, elevated surface. */
	surfaceEmphasis?: "default" | "primary";
}

export function SectionPanel({
	title,
	caption,
	action,
	link,
	children,
	surfaceEmphasis = "default",
}: SectionPanelProps) {
	return (
		<Card
			className={cn(
				"h-full hover:shadow-sm",
				surfaceEmphasis === "primary" &&
					"border-warm-border-strong border-t-[3px] border-t-ink bg-paper-2 shadow-pop"
			)}
		>
			<div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3">
				<div>
					<h3 className="text-dash-title font-semibold text-foreground">{title}</h3>
					{caption && (
						<p className="mt-0.5 text-dash-context text-muted-foreground">{caption}</p>
					)}
				</div>
				{(action || link) && (
					<div className="flex items-center gap-2">
						{action}
						{link && (
							<Button
								variant="ghost"
								size="sm"
								asChild
								className="min-h-11 text-coral"
							>
								<Link to={link.to}>
									{link.label}
									<ChevronRight className="ml-1 h-4 w-4" />
								</Link>
							</Button>
						)}
					</div>
				)}
			</div>
			{children}
		</Card>
	);
}
